/**
 * Pure derivation helpers for the Reservations Calendar (DNKADM-0032 U1).
 *
 * The Bay-Schedule design assumes dimensions the `/admin/reservations` contract
 * doesn't fully carry. Each is built **forward-compatibly**: derive it when the data
 * is present, **degrade gracefully** when absent, and **never throw**. Everything in
 * this module is a pure function of a parsed {@link Reservation} (or a primitive), so
 * it is exhaustively unit-tested and shared by every calendar layout (U2–U4).
 *
 * The derivations:
 *  - {@link deriveBay} — the booking's **bay** (the grid's rows/columns), resolved from
 *    the reservation's FIRST-CLASS `assignedResourceId`/`assignedResourceName`
 *    (DNKADM-0210 U1) — the same v2 resource the drawer renders, and the same id
 *    namespace as the `GET /v2/admin/resources` roster lanes, so a booking always
 *    lands on its roster lane. When no resource is assigned → an "Unassigned" lane;
 *    when NO booking yields a bay the whole grid degrades to a single lane (see
 *    {@link deriveBaySet}). ALL bay logic lives here.
 *  - {@link deriveServiceColor} — a **stable** service → color mapping (hash the
 *    service name to one of the design's six service palette entries; unknown/blank
 *    → a neutral default).
 *  - {@link deriveStatusBucket} — the free-text GHL `status` → the three design
 *    buckets (Confirmed / Pending / Cancelled; unrecognized → neutral, never throws).
 *  - window helpers — booked hours ({@link bookedHours}), the envelope axis
 *    ({@link dayEnvelope}) and THE single load definition ({@link loadPercent},
 *    DNKADM-0300 — booked hours over the location's real operating window,
 *    now→close on today); cancelled bookings excluded.
 *  - day-grid positioning — the fractional **venue** hour-of-day of an instant
 *    ({@link localHourOf}) and the "now" line position ({@link nowHourFraction}),
 *    used by the U2 day layouts to place blocks on the hour axis. Both take the
 *    selected location's IANA `timeZone` (DNKADM-0210 U2) so the grid is positioned
 *    on the VENUE clock, not the operator's browser clock; without a usable zone they
 *    fail soft to the instant's own ISO offset (the same reading the drawer shows).
 */
import type { BookedByActor, PaymentResolveOutcome, Reservation } from '@/api/reservations';
import type { WeeklyHoursDay } from '@/api/operatingHours';
import { buildCalendarMaps, type ServiceColor } from '@/theme/maps/calendar';
import { isAwaitingConfirmation } from '@/domain/v2Enums';
import { formatCreditAmount, formatMoney } from '@/utils/v2Format';
import { dateLabel, isoClockAsIs } from '@/utils/clockTime';
import { isCreditFunded } from '../reservationPayment';

// --- Operating-window capacity (design constants) ------------------------------

/**
 * Fallback window start/end hours (9a–9p) — the DEFAULT axis when a location's
 * real operating hours are unknown, and the reschedule wizard's slot window
 * default. Since DNKADM-0300 the LOAD metric never uses these (it reads the
 * real `dayOperating` window or renders `—`).
 */
export const OPERATING_HOUR_START = 9;
export const OPERATING_HOUR_END = 21;

// --- Bay identity --------------------------------------------------------------

/** "Unassigned" bay key + label — the fallback lane when no bay can be derived. */
export const UNASSIGNED_BAY_KEY = '__unassigned__';
export const UNASSIGNED_BAY_LABEL = 'Unassigned';

/** A derived bay: a stable `key` (grid identity) + a human `label`. */
export interface DerivedBay {
  key: string;
  label: string;
}

/** Calendar roster lane derived from the v2 resource list. */
export interface BayRosterLane {
  id: string;
  label: string;
}

/**
 * Derive a booking's **bay** from its first-class assigned resource (DNKADM-0210 U1).
 * When `assignedResourceId` is present it IS the stable bay key — the same v2 resource
 * id the roster lanes ({@link deriveBaySet}) carry — and `assignedResourceName` is the
 * human label (falling back to the id when the name is absent/blank). Otherwise → the
 * single **"Unassigned"** lane.
 *
 * This supersedes the legacy staff-as-bay alias (a `staff` with
 * `lastName === "ServiceAccount"`): that alias is only synthesized when a booking has
 * NO coach (`src/api/reservations.ts` `buildStaffAlias`), so a service with **both** a
 * coach and a resource used to fall through to "Unassigned" even though its resource
 * was known. Reading the resource directly fixes that and is unchanged for
 * resource-only bookings (the alias carried the same resource id). The `staff` alias
 * itself is untouched — the By-Staff axis ({@link deriveStaffOf}) still uses it.
 *
 * Never throws.
 */
export function deriveBay(reservation: Reservation): DerivedBay {
  const id = reservation.assignedResourceId?.trim();
  if (id) {
    // A blank/absent resource name degrades to the id so a lane always has a label.
    const label = reservation.assignedResourceName?.trim() || id;
    return { key: id, label };
  }
  return { key: UNASSIGNED_BAY_KEY, label: UNASSIGNED_BAY_LABEL };
}

/** True when a derived bay is a real (non-"Unassigned") bay. */
function isRealBay(bay: DerivedBay): boolean {
  return bay.key !== UNASSIGNED_BAY_KEY;
}

/**
 * Derive the **set of bays** (grid rows/columns), in the natural "Bay N" order when the
 * label matches `/^Bay\s*\d+/` (else alphabetically). The bay axis has two sources
 * (DNKADM-0040):
 *
 *  - **Roster-driven** (the primary path): when a `roster` is given (the tenant's bays
 *    from `GET /v2/admin/resources`), the rows are **the roster bays — always shown,
 *    regardless of bookings** (so an empty day still renders one row per bay instead of
 *    a blank grid). The **"Unassigned"** lane is appended only when some booking doesn't
 *    match a roster bay. An empty roster (`[]`) is treated as **no roster** — it carries
 *    no axis, so it falls through to the bookings-derived path below.
 *  - **Bookings-derived** (the fail-soft fallback): with no roster (the v2 resource
 *    list failed/degraded), the rows are the distinct real bays scanned **from the
 *    loaded bookings** — exactly today's behavior. When NO booking yields a real bay the
 *    grid degrades to a single **"Unassigned"** lane (so bookings still render by time);
 *    "Unassigned" is appended after the real bays only if some booking needs it.
 *
 * Pure; never throws. {@link deriveBay}'s matching rule is unchanged — only the SOURCE
 * of the axis differs, and a roster bay's `id` is the same key a booking's `deriveBay`
 * resolves to, so the two line up.
 */
export function deriveBaySet(
  reservations: Reservation[],
  roster?: BayRosterLane[],
): DerivedBay[] {
  // Roster-driven axis: the roster bays are ALWAYS shown (even with zero bookings),
  // plus an "Unassigned" lane when an unmatched booking exists. An empty roster carries
  // no axis → fall through to the bookings-derived path.
  if (roster && roster.length > 0) {
    const rosterBays = roster.map((b): DerivedBay => ({ key: b.id, label: b.label }));
    const known = new Set(rosterBays.map((b) => b.key));
    const hasUnmatched = reservations.some((r) => {
      const bay = deriveBay(r);
      return !isRealBay(bay) || !known.has(bay.key);
    });
    const sorted = [...rosterBays].sort(compareBayLabels);
    if (hasUnmatched) {
      sorted.push({ key: UNASSIGNED_BAY_KEY, label: UNASSIGNED_BAY_LABEL });
    }
    return sorted;
  }

  // Bookings-derived axis (the fail-soft fallback — today's behavior).
  const real = new Map<string, DerivedBay>();
  let hasUnassigned = false;
  for (const r of reservations) {
    const bay = deriveBay(r);
    if (isRealBay(bay)) real.set(bay.key, bay);
    else hasUnassigned = true;
  }

  // No real bay anywhere → the whole grid degrades to one "Unassigned" lane.
  if (real.size === 0) {
    return [{ key: UNASSIGNED_BAY_KEY, label: UNASSIGNED_BAY_LABEL }];
  }

  const sorted = [...real.values()].sort(compareBayLabels);
  if (hasUnassigned) {
    sorted.push({ key: UNASSIGNED_BAY_KEY, label: UNASSIGNED_BAY_LABEL });
  }
  return sorted;
}

/** Trailing "Bay N" number, or null when the label isn't a "Bay N". */
function bayNumber(label: string): number | null {
  const m = /^Bay\s*(\d+)/i.exec(label.trim());
  return m ? Number(m[1]) : null;
}

/** Sort "Bay N" labels numerically; otherwise alphabetically (case-insensitive). */
function compareBayLabels(a: DerivedBay, b: DerivedBay): number {
  const na = bayNumber(a.label);
  const nb = bayNumber(b.label);
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
}

// --- Staff identity (DNKADM-0041 — the By-Staff axis) --------------------------

/** "Unassigned" staff key + label — the lane for a booking with no resolved coach. */
export const UNASSIGNED_STAFF_KEY = '__unassigned__';
export const UNASSIGNED_STAFF_LABEL = 'Unassigned';

/** A derived coach lane: a stable `key` (grid identity) + a human `label`. */
export interface DerivedStaff {
  key: string;
  label: string;
}

/** Calendar staff lane derived from the v2 staff list. */
export interface StaffRosterLane {
  id: string;
  label: string;
  /** The coach's first name, for the "Coaches blocked today" line (DNKADM-0388 U2). */
  firstName?: string;
}

/**
 * Derive a booking's **coach** for the By-Staff axis. The coach is the booking's
 * `staff` object (DNKAPI-0084, resolved from `serviceStaffId`): its `id` is the stable
 * lane key and `staff.name` is the label, falling back to `firstName` then the `id`
 * (the existing label rules). A booking with **no resolved coach** — no `staff`, or a
 * `staff` with no usable `id` (a pure bay rental / unassigned) — groups under the single
 * **"Unassigned"** lane so nothing silently disappears (the Open Question default).
 * Never throws.
 */
export function deriveStaffOf(reservation: Reservation): DerivedStaff {
  const staff = reservation.staff;
  const id = staff?.id?.trim();
  if (staff && id) {
    const label = staff.name ?? staff.firstName ?? id;
    return { key: id, label };
  }
  return { key: UNASSIGNED_STAFF_KEY, label: UNASSIGNED_STAFF_LABEL };
}

/** True when a derived coach is a real (non-"Unassigned") coach. */
function isRealStaff(staff: DerivedStaff): boolean {
  return staff.key !== UNASSIGNED_STAFF_KEY;
}

/**
 * Sort coach labels alphabetically (case-insensitive), Unassigned compared as
 * text. Exported (DNKADM-0298) so the Now & Next coach-load chart orders its
 * bars exactly as the Grid's By-Staff axis orders its rows — one comparator,
 * two surfaces that agree. Structurally typed so any `{ label }` row sorts.
 */
export function compareStaffLabels(a: { label: string }, b: { label: string }): number {
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
}

/**
 * Derive the **set of coaches** (grid rows), sorted by name. Mirrors
 * {@link deriveBaySet}: the axis has two sources (DNKADM-0041):
 *
 *  - **Roster-driven** (the primary path): when a `roster` is given (the tenant's real
 *    staff from `GET /v2/admin/staff`), the rows are **the roster coaches — always shown,
 *    regardless of bookings** (so an empty day still renders one row per coach). The
 *    **"Unassigned"** lane is appended only when some booking has no resolved coach (or a
 *    coach not on the roster). An empty roster (`[]`) is treated as **no roster** — it
 *    carries no axis, so it falls through to the bookings-derived path below.
 *  - **Bookings-derived** (the fail-soft degrade): with no roster (the v2 staff list
 *    failed/degraded), the rows are the distinct coaches scanned **from the
 *    loaded bookings**. When NO booking yields a real coach the grid degrades to a single
 *    **"Unassigned"** lane; "Unassigned" is appended after the real coaches only if some
 *    booking needs it.
 *
 * Pure; never throws. {@link deriveStaffOf}'s matching rule is unchanged — only the
 * SOURCE of the axis differs, and a roster coach's `id` is the same key a booking's
 * `deriveStaffOf` resolves to, so the two line up.
 */
export function deriveStaffSet(
  reservations: Reservation[],
  roster?: StaffRosterLane[],
): DerivedStaff[] {
  // Roster-driven axis: the roster coaches are ALWAYS shown (even with zero bookings),
  // plus an "Unassigned" lane when a booking has no resolved/known coach. An empty roster
  // carries no axis → fall through to the bookings-derived path.
  if (roster && roster.length > 0) {
    const rosterStaff = roster.map((s): DerivedStaff => ({ key: s.id, label: s.label }));
    const known = new Set(rosterStaff.map((s) => s.key));
    const hasUnmatched = reservations.some((r) => {
      const coach = deriveStaffOf(r);
      return !isRealStaff(coach) || !known.has(coach.key);
    });
    const sorted = [...rosterStaff].sort(compareStaffLabels);
    if (hasUnmatched) {
      sorted.push({ key: UNASSIGNED_STAFF_KEY, label: UNASSIGNED_STAFF_LABEL });
    }
    return sorted;
  }

  // Bookings-derived axis (the fail-soft degrade — coaches present in the bookings).
  const real = new Map<string, DerivedStaff>();
  let hasUnassigned = false;
  for (const r of reservations) {
    const coach = deriveStaffOf(r);
    if (isRealStaff(coach)) real.set(coach.key, coach);
    else hasUnassigned = true;
  }

  // No real coach anywhere → the whole grid degrades to one "Unassigned" lane.
  if (real.size === 0) {
    return [{ key: UNASSIGNED_STAFF_KEY, label: UNASSIGNED_STAFF_LABEL }];
  }

  const sorted = [...real.values()].sort(compareStaffLabels);
  if (hasUnassigned) {
    sorted.push({ key: UNASSIGNED_STAFF_KEY, label: UNASSIGNED_STAFF_LABEL });
  }
  return sorted;
}

// --- Resource axis abstraction (DNKADM-0041) -----------------------------------

/**
 * A grid **row resource** — one lane on the calendar's left-side axis. The day/week
 * layouts render a list of these (rows or columns) and place each booking on the lane
 * its {@link ResourceAxis.keyOf} resolves to. `key` is the stable lane identity; `label`
 * is the human row label. Both the **bay** and **staff** axes produce these, so a layout
 * consumes them identically and only the SOURCE (which axis) differs.
 */
export interface ResourceLane {
  key: string;
  label: string;
}

/**
 * The **resource axis** a calendar grid groups by — the small abstraction (DNKADM-0041)
 * that lets the same layouts render **either** the bay axis **or** the staff axis. It
 * pairs the ordered set of lanes (`lanes`) with a pure per-booking lane resolver
 * (`keyOf`) and the "unassigned" lane key, so a layout can group `reservations` by
 * `keyOf(r)` against `lanes` without knowing whether it is grouping by bay or by coach.
 */
export interface ResourceAxis {
  /** The ordered lanes (rows/columns) — the bay set or the staff set. */
  lanes: ResourceLane[];
  /** The lane key a booking belongs to (`deriveBay(r).key` / `deriveStaffOf(r).key`). */
  keyOf: (reservation: Reservation) => string;
  /** The fallback lane key for an unresolved resource (`UNASSIGNED_*_KEY`). */
  unassignedKey: string;
}

/**
 * The **bay** {@link ResourceAxis} (today's behavior — DNKADM-0032 + 0040). `lanes` is
 * {@link deriveBaySet}(reservations, roster) and `keyOf` is `deriveBay(r).key`, so it is
 * byte-identical to the existing bay grouping; the abstraction merely names it.
 */
export function bayAxis(
  reservations: Reservation[],
  roster?: BayRosterLane[],
): ResourceAxis {
  return {
    lanes: deriveBaySet(reservations, roster),
    keyOf: (reservation) => deriveBay(reservation).key,
    unassignedKey: UNASSIGNED_BAY_KEY,
  };
}

/**
 * The **staff** {@link ResourceAxis} (DNKADM-0041) — the By-Staff equivalent of
 * {@link bayAxis}. `lanes` is {@link deriveStaffSet}(reservations, roster) and `keyOf`
 * is `deriveStaffOf(r).key`, placing each booking on its coach's row (∪ "Unassigned").
 */
export function staffAxis(
  reservations: Reservation[],
  roster?: StaffRosterLane[],
): ResourceAxis {
  return {
    lanes: deriveStaffSet(reservations, roster),
    keyOf: (reservation) => deriveStaffOf(reservation).key,
    unassignedKey: UNASSIGNED_STAFF_KEY,
  };
}

// --- Service color -------------------------------------------------------------

// `ServiceColor` (the strong `main` border/accent + the soft `bg` fill) is defined in
// the theme's calendar map (DNKADM-0271) — re-exported here so existing call sites
// (`import type { ServiceColor } from './derive'`) keep working unchanged.
export type { ServiceColor };

/** Neutral default color (light mode) for an unknown / blank service title. */
export const NEUTRAL_SERVICE_COLOR: ServiceColor = buildCalendarMaps('light').neutralService;

/**
 * Map a service name → a **stable** color from the mode's service palette
 * ({@link buildCalendarMaps}) by hashing the (trimmed, lower-cased) name, so the same
 * service is always the same color across renders and layouts. A blank / null name →
 * the mode's neutral color. `mode` defaults to `'light'` (byte-identical to the
 * pre-0271 behavior) for callers with no mounted theme (e.g. tests).
 */
export function deriveServiceColor(
  name: string | null,
  mode: 'light' | 'dark' = 'light',
): ServiceColor {
  const maps = buildCalendarMaps(mode);
  const key = name?.trim().toLowerCase();
  if (!key) return maps.neutralService;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % maps.servicePalette.length;
  // servicePalette is a non-empty tuple, so the modulo index is always present.
  return maps.servicePalette[idx] as ServiceColor;
}

// --- Status bucket -------------------------------------------------------------

/** The three design status buckets, plus a neutral fallback for unknown statuses. */
export type StatusBucket = 'confirmed' | 'pending' | 'cancelled' | 'neutral';

/**
 * Map a reservation `status` → one of the three design buckets (Confirmed / Pending /
 * Cancelled). Recognizes the v2 §4.8 vocabulary (`new|confirmed|cancelled|showed|
 * no_show|invalid`, DNKADM-0061) AND the legacy free-text values for back-compat:
 *  - cancelled / `no_show` / `invalid` → **cancelled** (dimmed/struck);
 *  - `new` (an unconfirmed hold) / `unconfirmed` / `pending` / `requested` →
 *    **pending** (dashed) — `unconfirmed` is anchored so it wins BEFORE the
 *    confirmed line's `/confirm/` can substring-match inside it (DNKADM-0277);
 *  - `confirmed` / `showed` / `booked` / `active` / `scheduled` → **confirmed**;
 *  - anything unrecognized (or null) → `neutral` (rendered as-is, never a throw).
 */
export function deriveStatusBucket(status: string | null): StatusBucket {
  if (!status) return 'neutral';
  const s = status.trim().toLowerCase();
  if (/cancel|no_show|invalid/.test(s)) return 'cancelled';
  if (isAwaitingConfirmation(s) || /pending|request/.test(s)) return 'pending';
  if (/book|confirm|active|scheduled|showed/.test(s)) return 'confirmed';
  return 'neutral';
}

// --- Payment state (DNKADM-0350 U2/U3) ------------------------------------------

/**
 * The FOUR rendered payment states — Paid / Due / Comp / Refunded (Operator
 * ruling, post-ship on DNKADM-0350: `refunded` was originally folded into
 * `'comp'`; the Operator rejected that — "Comp means deliberately free;
 * Refunded means money was taken and given back. Labelling it Comp tells
 * staff something false and hides that money moved." `Refunded` renders
 * neutral grey, the SAME visual weight as `Comp` — informative, never a task
 * to chase — but is its own distinct label). Credit-funded is a separate
 * INPUT condition (the member already paid for the credit) that collapses
 * into `'paid'` rather than its own render, per the spec ("Credit-funded →
 * treat as paid") — the Operator's ruling explicitly reconfirmed this stays.
 */
export type PaymentState =
  | 'paid'
  | 'due'
  | 'comp'
  | 'refunded'
  | 'unpaid'
  | 'in-progress'
  | 'needs-review';

/**
 * Whether the booking is a priced pay-later one nobody has taken payment for yet —
 * dink-api's `paymentStatus: 'unpaid'` (DNKAPI-0559 U5). It is a LABEL: nothing in this
 * console charges an unpaid booking on its own (Operator guardrail, 2026-09-22).
 */
export function isUnpaid(reservation: Reservation): boolean {
  return (reservation.paymentStatus ?? '').toLowerCase() === 'unpaid';
}

/**
 * Whether a take-payment attempt was started for this booking and pay-api never
 * confirmed its outcome — dink-api's `paymentStatus: 'pending'` **carrying an
 * `amountDue`** (Operator decision, 2026-09-22). **The money may have moved**, so it is
 * shown wherever Unpaid is shown, with its amount — but it is NOT payable from the
 * console: resolving it needs the original idempotency key, which this app no longer
 * holds. The desk checks the payment in pay-api before charging again.
 *
 * **`amountDue` is the discriminator, not `pending` on its own.** dink-api writes
 * `pending` for two different things, and only one of them is a problem:
 *  - **`pending` + `amountDue` = a stranded take-payment claim** (the server keys it off
 *    the take-payment idempotency key, DNKAPI-0559 / V167) — this state;
 *  - **`pending` alone = a live, in-flight hold** (a guest/paid booking mid-charge, a
 *    `NEW` row that settles or expires within minutes) — NOT this state, and not a task:
 *    it keeps whatever it rendered before this unit existed, with no new copy anywhere.
 */
export function isPaymentInProgress(reservation: Reservation): boolean {
  const due = reservation.amountDue ?? null;
  return (reservation.paymentStatus ?? '').toLowerCase() === 'pending' && due !== null && due > 0;
}

/**
 * Whether the booking's payment is waiting on a manager — dink-api's `paymentNeedsReview`
 * (an OPEN `RESERVATION_PAYMENT_NEEDS_REVIEW` notification, DNKAPI-0563). Nothing is
 * charged or refunded by it; it is a LABEL until someone closes the payment out.
 */
export function isPaymentNeedsReview(reservation: Reservation): boolean {
  return reservation.paymentNeedsReview === true;
}

/**
 * Derive a reservation's payment state from the fields the payload already
 * carries — `paymentStatus` (DNKAPI-0502, lowercase `pending|paid|declined|
 * refunded` or null), `creditsCharged`, `priceCharged` + `taxCharged`. No new
 * API field (the spec's explicit constraint).
 *
 * Priority, and why:
 *  1. **Credit-funded by a KNOWN credit charge** (`creditsChargedKnown` is not
 *     false, and {@link isCreditFunded} says so) → `'paid'` — the member already
 *     paid for the credit, checked FIRST because `priceCharged` can still carry
 *     the service's retail value even when credits covered it. An UNKNOWN credits
 *     read (the field absent on the wire) is not proof of funding for a display
 *     badge: it falls through to the `paymentStatus` rules below (DNKADM-0437
 *     V-20). The charge dialogs still use `isCreditFunded`'s fail-safe.
 *  2. `paymentStatus === 'refunded'` → `'refunded'` — its OWN state (Operator
 *     ruling), checked before the non-positive-total check below: an explicit
 *     "money moved and came back" signal outranks a $0 total's more generic
 *     "nothing to collect" reading.
 *  3. **A non-positive total** (`priceCharged + taxCharged <= 0` — nothing to
 *     collect, nothing owed) → `'comp'`, checked before the remaining
 *     `paymentStatus` cases so a genuinely free booking never reads "Due"
 *     regardless of what the status text says.
 *  4. `paymentStatus === 'paid'` → `'paid'`.
 *  5. Everything else — `declined` / `null`, and a `pending` with no `amountDue`
 *     (an in-flight hold), i.e. nothing collected yet or a collection attempt
 *     that didn't land — → `'due'`: the front
 *     desk's "collect at check-in" task, never red (a declined attempt is
 *     still a task, not an error, per the spec). The one exception: unknown
 *     credits AND no `paymentStatus` at all, with a positive total, → `null`
 *     (nothing is known about payment, so no badge or banner is shown).
 * Never throws.
 */
export function derivePaymentState(reservation: Reservation): PaymentState | null {
  // DNKADM-0408 U1: an OPEN needs-review notification outranks everything the payment
  // fields say — it is the server telling us money moved in a way it would not settle on
  // its own (DNKAPI-0563), so whatever `paymentStatus` reads, a manager must look. It
  // outranks `in-progress` in particular: a checked stranded payment that came back
  // needs-review is still `pending` + `amountDue` on the wire.
  if (isPaymentNeedsReview(reservation)) return 'needs-review';
  // DNKADM-0437 V-20: an UNKNOWN credits read (`creditsChargedKnown === false`, the field
  // absent on the wire) is not proof of credit funding. The shared `isCreditFunded` still
  // fails safe for the charge dialogs, but a "Paid" BADGE needs evidence: only a known
  // credit charge reads as paid here; unknown credits fall through to `paymentStatus`.
  if (
    reservation.creditsChargedKnown !== false &&
    isCreditFunded(reservation.creditsCharged, reservation.creditsChargedKnown)
  ) {
    return 'paid';
  }
  if (reservation.paymentStatus === 'refunded') return 'refunded';
  // DNKADM-0402 U5: `unpaid` is the server's own explicit "priced, and nobody has taken
  // the money yet" — it REPLACES the generic `due` for this state and outranks the
  // non-positive-total check below, which only ever sees a $0 total when the server
  // would not have marked it unpaid at all.
  if (isUnpaid(reservation)) return 'unpaid';
  // Operator decision, 2026-09-22: a `pending` booking THAT CARRIES AN `amountDue` is a
  // take-payment attempt whose outcome pay-api never confirmed — money that MAY have
  // moved. It used to fold into the generic `due` (rule 5 below), which read as "collect
  // this at check-in" and would have sent the desk to charge a card that may already
  // have been charged. It is now its own state, said in its own words, and carries no
  // payment action. A `pending` row WITHOUT an `amountDue` is a live in-flight hold and
  // still falls through to rule 5 exactly as it always did — see {@link
  // isPaymentInProgress}.
  if (isPaymentInProgress(reservation)) return 'in-progress';
  const total = (reservation.priceCharged ?? 0) + (reservation.taxCharged ?? 0);
  if (total <= 0) return 'comp';
  if (reservation.paymentStatus === 'paid') return 'paid';
  // DNKADM-0437 V-20: credits unknown AND no paymentStatus at all — nothing says what was
  // collected, so say nothing (no badge) rather than "Paid" or a guessed "Due".
  if (reservation.creditsChargedKnown === false && (reservation.paymentStatus ?? null) === null) {
    return null;
  }
  return 'due';
}

/** The short, always-present text token for a {@link PaymentState} — never color alone. */
export function paymentStateLabel(state: PaymentState): string {
  if (state === 'paid') return 'Paid';
  if (state === 'due') return 'Due';
  if (state === 'refunded') return 'Refunded';
  if (state === 'unpaid') return 'Unpaid';
  if (state === 'in-progress') return 'Payment in progress';
  if (state === 'needs-review') return 'Needs review';
  return 'Comp';
}

/**
 * The one line that tells the desk what to do about a payment pay-api never confirmed
 * (Operator decision, 2026-09-22). It is deliberately not an offer to charge: the
 * console cannot resolve this attempt, and charging again could take the money twice.
 */
export const PAYMENT_IN_PROGRESS_HELP =
  'Payment started but not confirmed. Check the payment in pay-api before charging again.';

/**
 * What Check payment found, in words (DNKADM-0408 U1) — shown inline under the drawer's
 * payment banner, not in a toast, because the desk acts on it. Keyed by the DNKAPI-0563
 * outcome. None of them claims a charge: Check payment never takes money.
 */
export const CHECK_PAYMENT_OUTCOME_TEXT: Record<PaymentResolveOutcome, string> = {
  paid: 'Payment found. This booking is paid.',
  unpaid: 'No charge went through. You can take payment again.',
  in_progress: "The card processor hasn't finished yet. Check again in a few minutes.",
  needs_review: 'This payment needs a manager. Nothing was charged or refunded.',
};

/**
 * The `unpaid` outcome for a session that cannot take payment itself (`canWrite` false, the
 * STAFF floor) — Operator decision, 2026-09-24. The floor has no Take payment, so "You can
 * take payment again" would name a control it does not have.
 */
export const CHECK_PAYMENT_UNPAID_NO_TAKE_PAYMENT_TEXT =
  'No charge went through. Ask a manager to take payment.';

/** The 409 `RESERVATION_PAYMENT_NOT_STRANDED` answer: someone (or the feed) already resolved it. */
export const CHECK_PAYMENT_NOTHING_TO_CHECK =
  'Nothing to check: this payment is no longer in progress.';

/**
 * The Close out success lines (DNKADM-0408 U2), shown inline under the banner like Check
 * payment's. Neither claims money moved: Close out never charges or refunds.
 */
export const CLOSE_OUT_MARKED_PAID_TEXT = 'Payment closed out. This booking is paid.';
export const CLOSE_OUT_MARKED_UNPAID_TEXT = 'Payment closed out. This booking is unpaid.';
/** `no_charge` on a cancelled booking: back to unpaid, and a cancelled booking owes nothing. */
export const CLOSE_OUT_CANCELLED_NOTHING_OWED_TEXT =
  'Payment closed out. Nothing is owed on this cancelled booking.';
/**
 * A cancelled booking a close-out marked paid (Architect decision, 2026-09-23). It is now an
 * ordinary paid booking, so the existing refund control applies; the console adds no refund
 * path of its own.
 */
export const CLOSE_OUT_CANCELLED_PAID_REFUND_TEXT = "An owner can refund this booking if it's owed.";
/** The 409 `RESERVATION_PAYMENT_NOT_STRANDED` answer to Close out: resolved elsewhere first. */
export const CLOSE_OUT_ALREADY_RESOLVED_TEXT =
  'This payment was already resolved. The booking shows where it stands now.';
/** A `403` to Close out: the control is withdrawn, and the session is told why. */
export const CLOSE_OUT_FORBIDDEN_TEXT = 'Only an owner or admin can close out a payment.';

/**
 * The other succeeded charges a `settle_to_payment` close-out found and left untouched —
 * staff refund them by hand (DNKAPI-0565). Shown until dismissed.
 */
export function closeOutOtherChargesText(paymentIds: readonly string[]): string {
  return `Other successful charges were found: ${paymentIds.join(', ')}. Refund them in the card processor.`;
}

/**
 * The amount a booking owes, formatted — the server's own `amountDue` in the booking's
 * own `currency` (DNKAPI-0559 U5, and, per the Operator decision of 2026-09-22, on a
 * `pending` booking too), falling back to the price + tax snapshot when an older read
 * carries no `amountDue`. `null` when neither resolves, so the caller shows the bare
 * label.
 *
 * Deliberately sourced from the RESERVATION, not from `tenant-settings`: the list and
 * calendar surfaces that render this badge must not add a read a STAFF session cannot
 * make (DNKADM-0364).
 *
 * **CALLER INVARIANT — pre-gate on the STATE, never call this on a raw `pending` row.**
 * The price+tax else-arm below is what makes an older `unpaid` read (one with no
 * `amountDue`) still show its figure. It is dead for a payment-in-progress row only
 * because {@link isPaymentInProgress} already required a positive `amountDue`, so every
 * caller reaching here with `in-progress` has one — nothing in this function enforces
 * that. Call it on a bare `paymentStatus === 'pending'` row and it will happily print a
 * price for a **live in-flight hold**, which the Operator's 2026-09-22 decision exists to
 * prevent: a hold is nobody's task and must never read as money owed.
 */
export function amountDueLabel(reservation: Reservation): string | null {
  const currency = reservation.currency ?? null;
  if (!currency) return null;
  const due = reservation.amountDue ?? null;
  const amount =
    due !== null && due > 0 ? due : (reservation.priceCharged ?? 0) + (reservation.taxCharged ?? 0);
  return amount > 0 ? formatMoney(amount, currency) : null;
}

/**
 * The badge/chip text for a payment state on the board and the lists (DNKADM-0402 U5):
 * an `unpaid` booking carries its amount ("Unpaid · $60.00") because that is the number
 * the desk has to collect, and an `in-progress` one carries it because that is the money
 * that may already have moved; every other state keeps the bare token it always showed.
 */
export function paymentBadgeText(reservation: Reservation, state: PaymentState): string {
  const label = paymentStateLabel(state);
  if (state !== 'unpaid' && state !== 'in-progress') return label;
  const amount = amountDueLabel(reservation);
  return amount === null ? label : `${label} · ${amount}`;
}

/**
 * The amount to ride with a payment-state label (U3's "show the amount where
 * there is one"): credits when the booking is credit-funded (what was
 * ACTUALLY charged), else the money total (`priceCharged + taxCharged`) when
 * known, positive, and a currency code is resolved; else null (a comp, or an
 * amount that can't be rendered yet) — the caller then shows the bare label.
 */
export function paymentAmountLabel(
  reservation: Reservation,
  currency: string | null,
): string | null {
  if (
    reservation.creditsChargedKnown !== false &&
    isCreditFunded(reservation.creditsCharged, reservation.creditsChargedKnown)
  ) {
    const credits = reservation.creditsCharged;
    if (credits === null) return null;
    return formatCreditAmount(credits);
  }
  // DNKADM-0402 U5: an unpaid booking shows what it OWES — the server's `amountDue`,
  // which is the exact amount Take payment will take.
  const due = reservation.amountDue ?? null;
  if ((isUnpaid(reservation) || isPaymentInProgress(reservation)) && due !== null && due > 0) {
    const code = currency ?? reservation.currency ?? null;
    if (code) return formatMoney(due, code);
  }
  const total = (reservation.priceCharged ?? 0) + (reservation.taxCharged ?? 0);
  if (total <= 0 || !currency) return null;
  return formatMoney(total, currency);
}

/**
 * How a booking was paid, in words (DNKADM-0454): "2 credits", "$65 card", "Pay later" or
 * "Comped"; null when the payload does not say (no price, no credits, no status). One shared
 * answer for every reservation card, row and drawer, so a card-paid booking never reads
 * "0 credits". Reads the same fields {@link derivePaymentState} does; a malformed currency code reads "Card".
 * A whole-dollar amount drops its cents ("$65 card"); the console cannot tell a desk card
 * from an online one on this payload, so a paid amount is worded "card".
 */
export function paymentHowLabel(reservation: Reservation, currency: string | null = null): string | null {
  if (
    reservation.creditsChargedKnown !== false &&
    isCreditFunded(reservation.creditsCharged, reservation.creditsChargedKnown) &&
    reservation.creditsCharged !== null
  ) {
    return formatCreditAmount(reservation.creditsCharged);
  }
  if (isUnpaid(reservation)) return 'Pay later';
  const total = (reservation.priceCharged ?? 0) + (reservation.taxCharged ?? 0);
  if (total > 0) {
    if (reservation.paymentStatus !== 'paid') return null;
    // Paid by money: the amount when a usable currency resolves, else the bare "Card".
    const code = currency ?? reservation.currency ?? null;
    if (!code) return 'Card';
    const fraction = Number.isInteger(total) ? 0 : undefined;
    try {
      const money = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction,
      }).format(total);
      return `${money} card`;
    } catch {
      // A malformed currency code (Intl RangeError): never throw from a label.
      return 'Card';
    }
  }
  // A zero total only reads "Comped" when the payload actually carried a price or a status.
  return reservation.priceCharged !== null || reservation.paymentStatus != null ? 'Comped' : null;
}

/**
 * The full banner/label text (U3): `"Paid · $60.00"`, `"Due · $60.00 at
 * check-in"`, `"Refunded · $60.00"` (the amount that moved and came back —
 * Operator ruling: never hide that money moved), or a bare label when no
 * amount resolves. `'due'` gets the "at check-in" qualifier (the spec's own
 * example wording); the others don't need it — the money is already settled
 * (or reversed) either way.
 */
export function paymentBannerText(state: PaymentState, amount: string | null): string {
  const label = paymentStateLabel(state);
  if (!amount) return label;
  return state === 'due' ? `${label} · ${amount} at check-in` : `${label} · ${amount}`;
}

// --- Booked by (DNKADM-0394 U3) ------------------------------------------------

/** The "Booked by" row's two lines: WHO placed the booking, and WHEN. */
export interface BookedByLine {
  /** "Front desk: info@simcreek.com" / "Member app: Sophia Craft" / "Guest checkout". */
  who: string;
  /** "Wed, Sep 16, 2026 at 10:59 PM", or null when the audit row carries no usable `at`. */
  when: string | null;
}

/**
 * The route in words, by actor class. The client owns this copy — `sessionMode` arrives
 * lowercase and server-owned (`'front-desk' | 'open'`), and it is a display HINT, never a
 * gate.
 *
 * **A null `sessionMode` must NOT read as "Front desk".** It means UNKNOWN, and — because
 * DNKAPI-0547 backfills nothing — it is what nearly every ADMIN booking already on staging
 * carries. So the ADMIN copy leads with "Staff", the fact the row does establish, and adds
 * the mode only when the row actually says one:
 *  - `front-desk` → "Front desk" (the spec's wording, on the rows that earned it);
 *  - `open`       → "Staff (open session)";
 *  - `null`       → a plain "Staff" — true of every admin booking, and it does not invent
 *                   a desk the row never claimed.
 *
 * Every branch is keyed on its OWN value and the tail is neutral — no class is reached by
 * elimination. An actor class this build does not know (a fifth `BookedByActorType`, or the
 * `null` a parse degrades an unrecognized one to) returns `null` and falls to the label-alone
 * path in {@link bookedByLine}, rather than inheriting ADMIN's copy and claiming a desk, a
 * session mode or a staff member it knows nothing about. That is the same rule
 * `asBookedSessionMode` applies to the mode one layer down, and the rule this file's own
 * derivations are built on: an unknown value must not claim something it doesn't know.
 */
function bookedByRoute(
  actorType: BookedByActor['actorType'],
  sessionMode: BookedByActor['sessionMode'],
): string | null {
  if (actorType === 'MEMBER') return 'Member app';
  if (actorType === 'GUEST') return 'Guest checkout';
  if (actorType === 'SYSTEM') return 'System';
  if (actorType === 'ADMIN') {
    if (sessionMode === 'front-desk') return 'Front desk';
    if (sessionMode === 'open') return 'Staff (open session)';
    return 'Staff';
  }
  return null;
}

/**
 * The drawer's "Booked by" content (DNKADM-0394 U3) — who placed the booking and when,
 * from the create-audit actor.
 *
 * Returns `null`, and the drawer omits the row entirely, when:
 *  - `actor` is `null` (no create-audit row — the spec's explicit requirement), or
 *  - the row carries neither a recognized `actorType` nor a `label`, leaving nothing true
 *    to say. A future actor class we don't know yet still renders its `label` alone rather
 *    than being dropped.
 *
 * `label` is appended only when present; the server sends `null` rather than an unresolved
 * id, so "Front desk" / "Member app" stands alone on a row it could not resolve and **no
 * principal id can reach the screen**. `at` is ISO-8601 with offset in the reservation's
 * location timezone and is rendered AS-IS, the same contract as `start`/`end` beside it.
 */
export function bookedByLine(actor: BookedByActor | null): BookedByLine | null {
  if (actor === null) return null;
  const route = bookedByRoute(actor.actorType, actor.sessionMode);
  if (route === null && actor.label === null) return null;
  const who =
    route === null
      ? actor.label!
      : actor.label === null
        ? route
        : `${route}: ${actor.label}`;
  const date = dateLabel(actor.at);
  const clock = isoClockAsIs(actor.at);
  return { who, when: date && clock ? `${date} at ${clock}` : null };
}

// --- Window math (utilization / load) ------------------------------------------

/** A booking's duration in hours, or 0 when either bound is missing / unparseable. */
export function durationHours(reservation: Reservation): number {
  const start = reservation.startTime ? Date.parse(reservation.startTime) : NaN;
  const end = reservation.endTime ? Date.parse(reservation.endTime) : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  const hours = (end - start) / 3_600_000;
  return hours > 0 ? hours : 0;
}

/**
 * Total **booked hours** across a set of bookings, **excluding cancelled** ones (a
 * cancelled booking frees the slot, so it doesn't count toward utilization).
 */
export function bookedHours(reservations: Reservation[]): number {
  return reservations.reduce((sum, r) => {
    if (deriveStatusBucket(r.status) === 'cancelled') return sum;
    return sum + durationHours(r);
  }, 0);
}

// --- Time-of-day positioning (day grids) ---------------------------------------

/**
 * Per-zone `Intl.DateTimeFormat` cache — the grid reads the venue clock once per
 * block, and constructing a formatter is far costlier than formatting with one. An
 * IANA string the runtime REJECTS (`Intl` throws `RangeError` on an unknown zone)
 * caches as `null`, so a bad `timezone` on the location record costs one throw for
 * the whole session and every caller then fails soft to the ISO offset.
 */
const zoneClockFormatters = new Map<string, Intl.DateTimeFormat | null>();
const zoneDateFormatters = new Map<string, Intl.DateTimeFormat | null>();

/** The memoized wall-clock formatter for an IANA zone, or null when unusable. */
function zoneClockFormatter(timeZone: string): Intl.DateTimeFormat | null {
  const cached = zoneClockFormatters.get(timeZone);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat | null;
  try {
    // `hourCycle: 'h23'` (not `hour12: false`) so midnight is `00`, never `24`.
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    formatter = null; // absent / invalid IANA zone — the caller falls back
  }
  zoneClockFormatters.set(timeZone, formatter);
  return formatter;
}

/** The memoized wall-date formatter for an IANA zone, or null when unusable. */
function zoneDateFormatter(timeZone: string): Intl.DateTimeFormat | null {
  const cached = zoneDateFormatters.get(timeZone);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat | null;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    formatter = null;
  }
  zoneDateFormatters.set(timeZone, formatter);
  return formatter;
}

/**
 * The fractional **wall-clock hour** an instant reads as in `timeZone`, or `null`
 * when the zone is unusable (so the caller can fall back). Never throws.
 */
function zoneHourFraction(ms: number, timeZone: string): number | null {
  const formatter = zoneClockFormatter(timeZone);
  if (!formatter) return null;
  let hour: number | null = null;
  let minute = 0;
  let second = 0;
  for (const part of formatter.formatToParts(new Date(ms))) {
    if (part.type === 'hour') hour = Number(part.value);
    else if (part.type === 'minute') minute = Number(part.value);
    else if (part.type === 'second') second = Number(part.value);
  }
  if (hour === null || !Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second))
    return null;
  return (hour % 24) + minute / 60 + second / 3600;
}

/**
 * The `YYYY-MM-DD` calendar date an instant reads as in `timeZone`, or `null` when
 * the zone is unusable (so the caller can fall back). Never throws.
 */
function zoneDateValue(ms: number, timeZone: string): string | null {
  const formatter = zoneDateFormatter(timeZone);
  if (!formatter) return null;
  let year: string | null = null;
  let month: string | null = null;
  let day: string | null = null;
  for (const part of formatter.formatToParts(new Date(ms))) {
    if (part.type === 'year') year = part.value;
    else if (part.type === 'month') month = part.value;
    else if (part.type === 'day') day = part.value;
  }
  return year && month && day ? `${year}-${month}-${day}` : null;
}

/** The `HH:mm(:ss)` clock written in an ISO instant — matches the drawer's `isoParts`. */
const ISO_CLOCK = /T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * The fractional hour an ISO instant reads as **at its OWN offset** — i.e. the clock
 * literally written in the string. This is the fail-soft reading when no venue zone
 * is usable: self-consistent (never shifted into some other zone) and identical to
 * what the detail drawer renders, which reads the wire string verbatim. A string
 * carrying no `T`-clock has no wall clock to read → `null` (the block is skipped
 * rather than positioned at a bogus offset).
 */
function isoClockHourOf(iso: string): number | null {
  const m = ISO_CLOCK.exec(iso);
  if (!m) return null;
  return Number(m[1]) + Number(m[2]) / 60 + (m[3] ? Number(m[3]) / 3600 : 0);
}

/** The `YYYY-MM-DD` calendar date written in an ISO instant. */
function isoDateValueOf(iso: string): string | null {
  const m = ISO_DATE.exec(iso);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/**
 * The **fractional hour-of-day** (0–24) of an ISO instant on the VENUE clock, used to
 * position a block on the day grids (the design positions blocks by `b.start`/`b.end`
 * as fractional hours). `timeZone` is the selected location's IANA zone
 * (DNKADM-0210 U2) — the grid must agree with the venue's own clock, NOT the
 * operator's browser (an operator in `-06:00` was seeing a `-05:00` 9:30 booking at
 * 8:30). **FAIL-SOFT:** when `timeZone` is absent/unresolved/invalid the reading falls
 * back to the instant's own ISO offset (see {@link isoClockHourOf}) — never
 * browser-local. A missing / unparseable instant → `null` (the caller skips the block
 * — never throws, never positions at a bogus offset).
 */
export function localHourOf(iso: string | null, timeZone?: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  if (timeZone) {
    const venue = zoneHourFraction(ms, timeZone);
    if (venue !== null) return venue;
  }
  return isoClockHourOf(iso);
}

/**
 * The current **fractional hour-of-day** (0–24) for the "now" line, read on the venue
 * clock when the location's IANA `timeZone` is known (DNKADM-0210 U2) so the line sits
 * where the venue's staff read it. Pulled from a `Date` (defaults to `new Date()`) so
 * tests can pin it. FAIL-SOFT: an absent/invalid zone reads the browser clock — a
 * `Date` carries no ISO offset of its own, so browser-local is the only total reading
 * left. Never throws.
 */
export function nowHourFraction(now: Date = new Date(), timeZone?: string | null): number {
  if (timeZone) {
    const ms = now.getTime();
    if (!Number.isNaN(ms)) {
      const venue = zoneHourFraction(ms, timeZone);
      if (venue !== null) return venue;
    }
  }
  return now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
}

// --- Visible hour range (operating window ∪ booking span; DNKADM-0039) ----------

/** An inclusive hour-axis range `[startHour, endHour]` (whole hours, in 0–24). */
export interface HourRange {
  startHour: number;
  endHour: number;
}

/**
 * The fixed default visible window (9a–9p) — the {@link visibleHourRange} fallback
 * for a **closed** day or when a location's operating hours can't be loaded. Still
 * extended by any out-of-hours booking so a reservation is never clipped.
 */
export const DEFAULT_HOUR_RANGE: HourRange = {
  startHour: OPERATING_HOUR_START,
  endHour: OPERATING_HOUR_END,
};

/**
 * The minimum spanned hours the axis renders — guards the open==close (or otherwise
 * degenerate) edge cases so a day never collapses to a 1-hour sliver.
 */
export const MIN_HOUR_SPAN = 4;

/** Clamp a fractional hour into `[0, 24]` (NaN → the bound). */
function clampHour(hour: number): number {
  if (!(hour >= 0)) return 0;
  return hour > 24 ? 24 : hour;
}

/**
 * The {@link WeeklyHoursDay} for a `YYYY-MM-DD` calendar date, looked up by its
 * weekday in the v2 operating-hours model's **0=Sunday … 6=Saturday** order
 * (DNKADM-0051 U2 — the §5.6 staff/resource convention). Returns null when there's no
 * schedule (hours unavailable) or no matching day — the caller then falls back to the
 * default window. Pure; never throws.
 */
export function operatingDayFor(
  days: WeeklyHoursDay[] | null,
  dateValue: string,
): WeeklyHoursDay | null {
  if (!days || days.length === 0) return null;
  const d = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const dow = d.getDay(); // JS 0=Sun…6=Sat — matches the v2 model directly
  return days.find((day) => day.dayOfWeek === dow) ?? null;
}

// --- Day envelope + the single load definition (DNKADM-0300) --------------------

/** The trimmed day axis plus the bookings that fell outside it. */
export interface DayEnvelope {
  /** The visible axis range. */
  range: HourRange;
  /** Bookings ending at/before the axis start (collapse to the early band). */
  early: Reservation[];
  /** Bookings starting at/after the axis end (collapse to the late band). */
  late: Reservation[];
}

/**
 * The day's **envelope axis** (DNKADM-0300 C — replaces the union rule for the
 * day grids): one hour of padding either side of the day's actual in-window
 * span, never wider than the operating window, never narrower than
 * {@link MIN_HOUR_SPAN} hours. Bookings entirely outside the returned range are
 * partitioned into `early`/`late` so the grid can collapse them to count bands
 * (tap-to-widen re-runs this with `expanded: true`, which restores the old
 * union — nothing is ever unreachable). A booking that merely STRADDLES an
 * edge stays a block (the renderer clips it to the axis).
 *
 * `operating` null (closed/unknown day) falls back to `fallback` (default
 * {@link DEFAULT_HOUR_RANGE}); a day with no in-window bookings renders the
 * whole window. Whole-hour bounds, clamped to [0, 24]. Pure; never throws.
 */
export function dayEnvelope(
  dayBookings: Reservation[],
  operating: HourRange | null,
  opts?: { expanded?: boolean; trim?: boolean; fallback?: HourRange; timeZone?: string | null },
): DayEnvelope {
  const fallback = opts?.fallback ?? DEFAULT_HOUR_RANGE;
  const timeZone = opts?.timeZone ?? null;
  const window: HourRange = operating ?? fallback;

  const spans = dayBookings
    .map((reservation) => {
      const start = localHourOf(reservation.startTime, timeZone);
      const end = localHourOf(reservation.endTime, timeZone);
      if (start === null || end === null || end <= start) return null;
      return { reservation, start, end };
    })
    .filter((s): s is { reservation: Reservation; start: number; end: number } => s !== null);

  let start: number;
  let end: number;
  if (opts?.expanded) {
    // The widened axis: the old union rule (window ∪ every span).
    start = window.startHour;
    end = window.endHour;
    for (const s of spans) {
      start = Math.min(start, s.start);
      end = Math.max(end, s.end);
    }
  } else if (opts?.trim === false) {
    // The full operating window, untrimmed — since the 2026-08-14 Operator
    // ruling this is the default axis in BOTH orientations (a stable axis
    // beats the trimmed one's reclaimed width for a board read all day; the
    // partition still collapses genuinely out-of-window bookings to bands).
    start = window.startHour;
    end = window.endHour;
  } else {
    const inWindow = spans.filter((s) => s.start < window.endHour && s.end > window.startHour);
    if (inWindow.length === 0) {
      start = window.startHour;
      end = window.endHour;
    } else {
      let first = Infinity;
      let last = -Infinity;
      for (const s of inWindow) {
        first = Math.min(first, s.start);
        last = Math.max(last, s.end);
      }
      start = Math.max(window.startHour, Math.floor(first) - 1);
      end = Math.min(window.endHour, Math.ceil(last) + 1);
    }
  }

  start = clampHour(Math.floor(start));
  end = clampHour(Math.ceil(end));
  if (end <= start) end = clampHour(start + 1);
  if (end - start < MIN_HOUR_SPAN) {
    // Widen toward the window's own bounds first, then wherever room remains.
    end = Math.min(24, Math.max(end, Math.min(window.endHour, start + MIN_HOUR_SPAN)));
    if (end - start < MIN_HOUR_SPAN) end = Math.min(24, start + MIN_HOUR_SPAN);
    if (end - start < MIN_HOUR_SPAN) start = Math.max(0, end - MIN_HOUR_SPAN);
  }

  const early: Reservation[] = [];
  const late: Reservation[] = [];
  if (!opts?.expanded) {
    for (const s of spans) {
      if (s.end <= start) early.push(s.reservation);
      else if (s.start >= end) late.push(s.reservation);
    }
  }
  return { range: { startHour: start, endHour: end }, early, late };
}

/**
 * **The single definition of "busy" in the app** (DNKADM-0300 D — replaces
 * `clippedUtilization`, which hardcoded 9–21 and ignored the clock): booked
 * hours over the location's REAL operating window, measured **from now to
 * close on today** (`from = max(now, open)`) and over the whole window on any
 * other day. Cancelled bookings contribute nothing; pending count as booked.
 *
 * Null discipline: no operating window, or `from >= close` (past close today,
 * or a zero-width window) → `null`, never 0% — the renderer shows `—`.
 * Capped at 100. `laneCount` scales capacity for a multi-lane reading (a
 * whole-day header over N bays); defaults to 1 (a single lane/row).
 *
 * `nowHour` is the VENUE-clock fractional hour (the caller passes
 * `nowHourFraction(now, timeZone)`) — required only when `isToday`.
 */
export function loadPercent(
  reservations: Reservation[],
  operating: HourRange | null,
  opts: { isToday: boolean; nowHour?: number; laneCount?: number; timeZone?: string | null },
): number | null {
  if (operating === null) return null;
  const from = opts.isToday
    ? Math.max(opts.nowHour ?? operating.startHour, operating.startHour)
    : operating.startHour;
  const to = operating.endHour;
  if (from >= to) return null;
  const timeZone = opts.timeZone ?? null;
  let booked = 0;
  for (const r of reservations) {
    if (deriveStatusBucket(r.status) === 'cancelled') continue;
    const start = localHourOf(r.startTime, timeZone);
    const end = localHourOf(r.endTime, timeZone);
    if (start === null || end === null) continue;
    booked += Math.max(0, Math.min(end, to) - Math.max(start, from));
  }
  const capacity = (to - from) * Math.max(1, opts.laneCount ?? 1);
  return Math.round(Math.min(1, booked / capacity) * 100);
}

/** The qualifier the load number always carries (which question it answered). */
export function loadQualifier(dateValue: string, todayDateValue: string): string {
  if (dateValue === todayDateValue) return 'rest of today';
  return dateValue < todayDateValue ? 'that day' : 'projected';
}

/**
 * The day's bookings falling entirely **outside the operating window**
 * (DNKADM-0300 E — the after-hours list): ended at/before open, or starting
 * at/after close, on the venue clock. A straddler is inside. No operating
 * window → `[]` (with no window there is no "outside"). Sorted by start.
 */
export function outsideOperating(
  reservations: Reservation[],
  operating: HourRange | null,
  timeZone?: string | null,
): Reservation[] {
  if (operating === null) return [];
  const outside = reservations.filter((r) => {
    const start = localHourOf(r.startTime, timeZone ?? null);
    const end = localHourOf(r.endTime, timeZone ?? null);
    if (start === null || end === null) return false;
    return end <= operating.startHour || start >= operating.endHour;
  });
  return [...outside].sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
}

// --- Week-grid math (DNKADM-0032 U3) -------------------------------------------

/** The local `YYYY-MM-DD` value for a `Date` (matches the shell's date model). */
export function toDateValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The `YYYY-MM-DD` calendar date of an ISO instant on the VENUE clock, or null when
 * missing / unparseable. `timeZone` is the selected location's IANA zone. FAIL-SOFT:
 * absent/unusable zone → the instant's own ISO date (the wire offset), then
 * browser-local for parseable non-ISO strings. Never throws.
 */
export function localDateValueOf(iso: string | null, timeZone?: string | null): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  if (timeZone) {
    const venue = zoneDateValue(ms, timeZone);
    if (venue !== null) return venue;
  }
  return isoDateValueOf(iso) ?? toDateValue(new Date(ms));
}

/**
 * The seven `Date`s of the Mon–Sun week containing `dateValue` (a `YYYY-MM-DD`),
 * in order Monday → Sunday. The week layouts group bookings by each date's local
 * value and use it as the "open this day" target.
 */
export function weekDates(dateValue: string): Date[] {
  const d = new Date(`${dateValue}T00:00:00`);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const deltaToMonday = (dow + 6) % 7; // Mon→0, Sun→6
  const monday = new Date(d);
  monday.setDate(d.getDate() - deltaToMonday);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }
  return days;
}

/** A booking packed onto a horizontal lane (its index) for overlap stacking. */
interface LanePlacement {
  reservation: Reservation;
  startHour: number;
  endHour: number;
  lane: number;
}

/** The packed lanes for a day's bookings, with the total lane count. */
export interface LanePacking {
  placed: LanePlacement[];
  lanes: number;
}

/**
 * **Lane-pack** a set of bookings so overlapping bookings stack onto distinct lanes
 * (the design's week-schedule packing). Bookings are sorted by start hour, then each
 * is placed on the first lane whose previous booking has already ended (`laneEnd <=
 * start`), opening a new lane when none is free. Bookings without a usable venue-hour
 * span (missing / unparseable times, or non-positive duration) are skipped — they
 * can't be positioned on the time axis. `timeZone` is the selected location's IANA
 * zone, so the packed spans sit on the same venue clock the week axis renders
 * (DNKADM-0210 U2). `lanes` is at least 1 so a day always has a row height. Never
 * throws.
 */
export function packLanes(reservations: Reservation[], timeZone?: string | null): LanePacking {
  const spans = reservations
    .map((reservation) => {
      const startHour = localHourOf(reservation.startTime, timeZone);
      const endHour = localHourOf(reservation.endTime, timeZone);
      if (startHour === null || endHour === null || endHour <= startHour) return null;
      return { reservation, startHour, endHour };
    })
    .filter((s): s is { reservation: Reservation; startHour: number; endHour: number } => s !== null)
    .sort((a, b) => a.startHour - b.startHour);

  const laneEnds: number[] = [];
  const placed: LanePlacement[] = spans.map((span) => {
    let lane = laneEnds.findIndex((end) => end <= span.startHour);
    if (lane < 0) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = span.endHour;
    return { ...span, lane };
  });

  return { placed, lanes: Math.max(1, laneEnds.length) };
}

// --- Reschedule wizard: categories + available-slot computation (U4) -----------

/** The kind of resource a booking category is reserved against. */
export type CategoryResource = 'bay' | 'coach';

/**
 * A booking **category** (the wizard's step-1 cards). `bucket` is the credit-balance
 * category name the "Your credits" chip pairs with (matched case-insensitively against
 * `GET /admin/members/{id}` `creditBalances`); `resource` decides whether step 2 picks
 * a Bay or a Coach (and labels Duration vs. Type accordingly). Derived from the
 * service taxonomy — see {@link RESCHEDULE_CATEGORIES} / {@link deriveServiceCategory}.
 */
export interface BookingCategory {
  /** Stable key (used as the selected-category value). */
  key: string;
  /** Card label, e.g. "Bay Reservation". */
  label: string;
  /** The credit bucket name this category draws from (matched to `creditBalances`). */
  bucket: string;
  /** Whether step 2 resolves a Bay or a Coach. */
  resource: CategoryResource;
}

/**
 * The four reschedule categories from the design (`CATS`). Bay Reservation / Club
 * Fitting reserve a **bay**; Lessons / Swing Evaluation reserve a **coach**. The
 * `bucket` names mirror the design's credit buckets and are matched case-insensitively
 * (ignoring non-alphanumerics) against the member's `creditBalances` category names.
 */
export const RESCHEDULE_CATEGORIES: readonly BookingCategory[] = [
  { key: 'bay_reservation', label: 'Bay Reservation', bucket: 'Bay Reservation Credits', resource: 'bay' },
  { key: 'club_fitting', label: 'Club Fitting', bucket: 'Club Fitting Credits', resource: 'bay' },
  { key: 'lessons', label: 'Lessons', bucket: 'Lesson Credits', resource: 'coach' },
  { key: 'swing_evaluation', label: 'Swing Evaluation', bucket: 'Swing Evaluation Credits', resource: 'coach' },
] as const;

/**
 * Map a booking's service name → its reschedule {@link BookingCategory}. Keyed off the
 * service-name taxonomy from the design (`SVC_CAT`): a name containing "lesson" →
 * Lessons; "fitting" → Club Fitting; "swing"/"evaluation" → Swing Evaluation; anything
 * else (rentals, league, events, open practice, unknown/blank) → Bay Reservation (the
 * default). Never throws — an unknown service still resolves to the first category so
 * the wizard always opens on a real card.
 */
export function deriveServiceCategory(serviceName: string | null): BookingCategory {
  const s = serviceName?.trim().toLowerCase() ?? '';
  let key: string;
  if (/lesson/.test(s)) key = 'lessons';
  else if (/fitting/.test(s)) key = 'club_fitting';
  else if (/swing|evaluation/.test(s)) key = 'swing_evaluation';
  else key = 'bay_reservation';
  // RESCHEDULE_CATEGORIES is a non-empty const tuple; the lookup always resolves.
  return (
    RESCHEDULE_CATEGORIES.find((c) => c.key === key) ?? (RESCHEDULE_CATEGORIES[0] as BookingCategory)
  );
}

/** Slot-grid step in hours (every 30 minutes, matching the design). */
const SLOT_STEP_HOURS = 0.5;

/** One candidate start-time slot in the reschedule wizard's step 3. */
export interface TimeSlot {
  /** The slot's start, as a fractional local hour-of-day (e.g. 13.5 = 1:30 PM). */
  startHour: number;
  /** True when the slot doesn't collide with another non-cancelled booking on the resource. */
  available: boolean;
}

/**
 * Compute the wizard's candidate start-time slots for a day, **client-side** (the
 * spec's model — no backend availability endpoint). A slot runs every
 * {@link SLOT_STEP_HOURS} across the operating window (`[start, end]`, default 9a–9p)
 * such that `slot + durationHours` still fits inside the window. A slot is
 * **available** when, for the chosen resource (a bay key or a coach id), no OTHER
 * non-cancelled booking on the SAME resource that day overlaps `[slot, slot+duration)`.
 *
 * `dayReservations` should already be the loaded window's bookings for the target day
 * + resource concern; `excludeBookingId` drops the booking being rescheduled from the
 * collision check (so it doesn't block its own slot). `resourceKey` is the bay key
 * (from {@link deriveBay}) for a bay category, or the `staff.id` for a coach category;
 * `resource` selects which. Pure + never throws (a zero/negative duration → no slots).
 */
export function availableSlots(
  dayReservations: Reservation[],
  options: {
    resource: CategoryResource;
    resourceKey: string | null;
    durationHours: number;
    excludeBookingId?: string | null;
    windowStart?: number;
    windowEnd?: number;
    timeZone?: string | null;
  },
): TimeSlot[] {
  const { resource, resourceKey, durationHours: dur } = options;
  const windowStart = options.windowStart ?? OPERATING_HOUR_START;
  const windowEnd = options.windowEnd ?? OPERATING_HOUR_END;
  if (!(dur > 0)) return [];

  // The other non-cancelled bookings on the SAME resource, as [start, end) hour spans.
  const busy: { start: number; end: number }[] = [];
  for (const r of dayReservations) {
    if (r.bookingId !== null && r.bookingId === options.excludeBookingId) continue;
    if (deriveStatusBucket(r.status) === 'cancelled') continue;
    const onResource =
      resource === 'coach' ? (r.staff?.id ?? null) === resourceKey : deriveBay(r).key === resourceKey;
    if (!onResource) continue;
    const start = localHourOf(r.startTime, options.timeZone);
    const end = localHourOf(r.endTime, options.timeZone);
    if (start === null || end === null || end <= start) continue;
    busy.push({ start, end });
  }

  const slots: TimeSlot[] = [];
  // Tolerance so floating-point accumulation (0.5 steps) doesn't drop the last slot.
  for (let t = windowStart; t + dur <= windowEnd + 1e-4; t += SLOT_STEP_HOURS) {
    const slotEnd = t + dur;
    const collides = busy.some((b) => t < b.end && slotEnd > b.start);
    slots.push({ startHour: Math.round(t * 100) / 100, available: !collides });
  }
  return slots;
}

